import {useAppState} from "@/store";
import Panel from "@/components/Panel";
import IconFeature from "@/components/IconFeature";
import MuscleIcon from "@/components/icons/MuscleIcon";
import HourglassIcon from "@/components/icons/HourglassIcon";
import TruckIcon from "@/components/icons/TruckIcon";
import RoadIcon from "@/components/icons/RoadIcon";
import RateChoice from "@/components/RateChoice";
import TimeChoice from "@/components/TimeChoice";
import {useEffect, useRef} from "react";
import EstimateContent from "@/components/EstimateContent";
import {dataLayerPush, formatPrice, parseMergeTags} from "@/helpers";
import Carousel from "@/components/Carousel";
import Inestimable from "@/components/steps/Inestimable";
import DOMPurify from "dompurify";

export default function Estimate() {

    const {state, setState} = useAppState()
    const timeSelectEl = useRef(null)
    const availIndex = state.availabilities?.findIndex(a => a.date === state.availability?.date)
    const initialSlide = useRef(availIndex === -1 ? 2 : Math.max(availIndex, 2))

    useEffect(() => {
        setState(s => {
            s.timeSelectEl = timeSelectEl.current
        })
    }, [state.estimate])

    const content = window.ein?.content?.step_5 || {}

    const {titles, subtitles, rate_description, rate_disclaimer, date_heading, time_heading} = content

    const inestimable = Object.values(state.inestimable).some(i => !!i)

    if (inestimable) {
        return <Inestimable/>
    }

    useEffect(() => {
        if (!inestimable) {
            dataLayerPush('book-estimate-view', {
                ['book-estimate-amount']: state.estimate?.totalCost,
                ['book-estimate-movers']: state.estimate?.movers || 0,
                ['book-estimate-hours']: state.estimate?.totalTime || 0,
                ['book-estimate-trucks']: state.estimate?.trucks || 0,
                ['book-estimate-trip-fees']: state.estimate?.tripFees || 0,
                ['book-estimate-initial-date']: state.availability?.date || null,
                ['book-assigned-branch']: state.branch?.name || null,
                ['book-estimate-id']: state.customerId || null,
            }).then()
        }
    }, [])

    const title = state.chosen_date ? titles.date_avail : titles.date_unavail
    const subtitle = state.chosen_date ? subtitles.date_avail : subtitles.date_unavail

    return (
        <>
            <div className={"grid grid-cols-4 lg:grid-cols-12 gap-x-[20px] lg:gap-x-[32px] gap-y-[40px]"}>
                <div className="col-span-4 lg:col-span-12 flex flex-col items-center text-center">
                    {title && (
                        <div className="heading-h1 mb-[8px]">
                            {parseMergeTags(title, state)}
                        </div>
                    )}
                    {subtitle && (
                        <div className="text-big mb-[8px]">
                            {parseMergeTags(subtitle, state)}
                        </div>
                    )}
                </div>
                <div className="col-span-4 lg:col-span-8 lg:col-start-3">
                    <Panel>
                        {(state.estimate || state.chosen_rate) && (
                            <>
                                <div className={"text-[72px] leading-[72px] font-black text-center mb-[8px]"}>
                                    {formatPrice(state.chosen_rate || state.estimate.totalCost)}
                                </div>
                                {rate_description && (
                                    <div className="text-brand-orange text-center mb-[32px]">
                                        {parseMergeTags(rate_description, state)}
                                    </div>
                                )}
                            </>
                        )}
                        <div className={"grid grid-cols-2 lg:grid-cols-4 mb-[24px] gap-y-[24px] px-[24px]"}>
                            <IconFeature icon={<MuscleIcon/>} title={`${state.estimate?.movers || '?'} movers`}/>
                            <IconFeature icon={<HourglassIcon/>} title={`${state.estimate?.totalTime || '?'} hours`}/>
                            <IconFeature icon={<TruckIcon/>} title={`${state.estimate?.trucks || '?'} x 26’ truck`}/>
                            {(typeof state.branch?.tripFees !== 'undefined') && (
                                <IconFeature icon={<RoadIcon/>} title={`\$${state.estimate?.tripFees} trip fee`}/>
                            )}
                        </div>
                        {rate_disclaimer && (
                            <div
                                className={"text-small italic text-brand-grey text-center mb-[24px] [&>:last-child]:mb-0"}
                                dangerouslySetInnerHTML={{'__html': DOMPurify.sanitize(parseMergeTags(rate_disclaimer, state))}}
                            />
                        )}
                        {(!state.availability || !state.availability?.startTimes?.length) && (
                            <div className={"font-bold text-red leading-[20px] text-center mb-[16px]"}>
                                Unfortunately, we don’t have availability on the day you selected.<br/>Please select another date below:
                            </div>
                        )}
                        <div ref={timeSelectEl}>
                            {date_heading && (
                                <div className={"font-bold text-center mb-[16px]"}>
                                    {date_heading}
                                </div>
                            )}
                            <div className={"mb-[40px]"}>
                                <Carousel
                                    className={'ein-rate-carousel lg:px-[64px]'}
                                    spaceBetween={8}
                                    slidesPerView={2}
                                    breakpoints={{
                                        360: {slidesPerView: 3},
                                        600: {slidesPerView: 4},
                                        768: {slidesPerView: 5},
                                        1024: {slidesPerView: 4},
                                        1280: {slidesPerView: 5},
                                    }}
                                    slides={state.availabilities}
                                    centerInsufficientSlides={true}
                                    centeredSlides={true}
                                    renderSlide={a => <RateChoice availability={a}/>}
                                    initialSlide={initialSlide.current}
                                />
                            </div>
                            {!!state.availability?.startTimes?.length && (
                                <>
                                    {time_heading && (
                                        <div className="heading-h4 text-center mb-[16px]">
                                            {time_heading}
                                        </div>
                                    )}
                                    <div className={"lg:flex lg:items-center lg:justify-between lg:gap-[8px]"}>
                                        {state.availabilities?.find(a => a.date === state.chosen_date?.raw)?.startTimes?.map((time, i) => (
                                            <div className={'flex-1 mb-[8px] last:mb-0 lg:mb-0'} key={i}>
                                                <TimeChoice startTime={time}/>
                                            </div>
                                        ))}
                                    </div>
                                    {/*<Carousel*/}
                                    {/*    className={'ein-rate-carousel lg:px-[64px]'}*/}
                                    {/*    spaceBetween={8}*/}
                                    {/*    slidesPerView={2}*/}
                                    {/*    breakpoints={{*/}
                                    {/*        360: {slidesPerView: 3},*/}
                                    {/*        600: {slidesPerView: 4},*/}
                                    {/*        768: {slidesPerView: 5},*/}
                                    {/*        1024: {slidesPerView: 4},*/}
                                    {/*        1280: {slidesPerView: 5},*/}
                                    {/*    }}*/}
                                    {/*    slides={state.availabilities?.find(a => a.date === state.chosen_date?.raw)?.startTimes}*/}
                                    {/*    centerInsufficientSlides={true}*/}
                                    {/*    centeredSlides={true}*/}
                                    {/*    renderSlide={time => <TimeChoice startTime={time}/>}*/}
                                    {/*/>*/}
                                </>
                            )}
                        </div>
                    </Panel>
                </div>
            </div>
            <EstimateContent/>
        </>
    )


}
